/**
 * HostManager Component
 *
 * Admin component for managing hosts with:
 * - Host list display
 * - Create new hosts via HostForm
 * - Edit and delete hosts
 * - Connection validation
 *
 * @module components/admin/HostManager
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Server,
  Plus,
  RefreshCw,
  Loader2,
  AlertCircle,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import HostForm from './HostForm.jsx';

// ============================================
// API Functions
// ============================================

/**
 * Fetch all hosts from the API
 * @returns {Promise<Array>} List of hosts
 */
async function fetchHosts() {
  const response = await fetch('/api/hosts?includeDisabled=true');
  if (!response.ok) {
    throw new Error(`Failed to fetch hosts: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Create a new host
 * @param {object} hostData - Host data
 * @returns {Promise<object>} Created host
 */
async function createHost(hostData) {
  const response = await fetch('/api/hosts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(hostData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to create host: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update an existing host
 * @param {string} id - Host ID
 * @param {object} hostData - Host data
 * @returns {Promise<object>} Updated host
 */
async function updateHost(id, hostData) {
  const response = await fetch(`/api/hosts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(hostData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to update host: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete a host
 * @param {string} id - Host ID
 * @returns {Promise<void>}
 */
async function deleteHost(id) {
  const response = await fetch(`/api/hosts/${id}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to delete host: ${response.statusText}`);
  }
}

/**
 * Validate host connection
 * @param {string} id - Host ID
 * @returns {Promise<object>} Validation results
 */
async function validateHostConnection(id) {
  const response = await fetch(`/api/hosts/${id}/validate`, {
    method: 'POST'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to validate host: ${response.statusText}`);
  }
  return response.json();
}

// ============================================
// Sub-Components
// ============================================

/**
 * Host list item
 */
function HostItem({ host, onEdit, onDelete, onValidate, isValidating }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${host.isOnline ? 'bg-green-500/10' : 'bg-gray-700'}`}>
              <Server className={`w-5 h-5 ${host.isOnline ? 'text-green-400' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-medium text-white">{host.name}</h3>
              <p className="text-sm text-gray-400">{host.hostname}:{host.port}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Status badges */}
            <span className={`px-2 py-1 rounded text-xs ${
              host.isEnabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
            }`}>
              {host.isEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
              host.isOnline ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              {host.isOnline ? (
                <><CheckCircle className="w-3 h-3" /> Online</>
              ) : (
                <><XCircle className="w-3 h-3" /> Offline</>
              )}
            </span>

            {/* Actions */}
            <button
              onClick={() => onValidate(host.id)}
              disabled={isValidating}
              className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
              title="Test Connection"
            >
              {isValidating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => onEdit(host)}
              className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(host.id)}
              className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-red-400"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 rounded hover:bg-gray-700 text-gray-400"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {host.description && (
          <p className="mt-2 text-sm text-gray-400">{host.description}</p>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-700 text-sm text-gray-400 space-y-1">
          <div><span className="text-gray-500">Username:</span> {host.username}</div>
          <div><span className="text-gray-500">Claude Path:</span> {host.claudePath}</div>
          <div><span className="text-gray-500">Skills Path:</span> {host.skillsPath}</div>
          <div><span className="text-gray-500">Workspace Path:</span> {host.workspacePath}</div>
          {host.agentCount !== undefined && (
            <div><span className="text-gray-500">Agents:</span> {host.agentCount}</div>
          )}
          {host.lastHealthCheck && (
            <div><span className="text-gray-500">Last Check:</span> {new Date(host.lastHealthCheck).toLocaleString()}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * HostManager Component
 *
 * Manages hosts with list display, create/edit form, and actions.
 */
export default function HostManager() {
  // State
  const [hosts, setHosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingHost, setEditingHost] = useState(null);
  const [validatingId, setValidatingId] = useState(null);

  // Load hosts
  const loadHosts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchHosts();
      setHosts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHosts();
  }, [loadHosts]);

  // Handlers
  const handleSubmit = useCallback(async (formData) => {
    if (editingHost) {
      await updateHost(editingHost.id, formData);
    } else {
      await createHost(formData);
    }
    await loadHosts();
    setShowForm(false);
    setEditingHost(null);
  }, [editingHost, loadHosts]);

  const handleEdit = useCallback((host) => {
    setEditingHost(host);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!confirm('Are you sure you want to delete this host?')) return;
    try {
      await deleteHost(id);
      await loadHosts();
    } catch (err) {
      alert(err.message);
    }
  }, [loadHosts]);

  const handleValidate = useCallback(async (id) => {
    setValidatingId(id);
    try {
      await validateHostConnection(id);
      await loadHosts();
    } catch (err) {
      alert(err.message);
    } finally {
      setValidatingId(null);
    }
  }, [loadHosts]);

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingHost(null);
  }, []);

  // Render
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <Server className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Host Management</h1>
            <p className="text-sm text-gray-400">Manage hosts for running Claude agents</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadHosts}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => { setEditingHost(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"
          >
            <Plus className="w-4 h-4" />
            Add Host
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 rounded-md bg-red-500/10 border border-red-500/30 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Form (slide-in panel) */}
      {showForm && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <HostForm
            initialValues={editingHost}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEditing={!!editingHost}
            submitLabel={editingHost ? 'Save Changes' : 'Create Host'}
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && !hosts.length && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !hosts.length && !showForm && (
        <div className="text-center py-12">
          <Server className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300">No hosts configured</h3>
          <p className="text-gray-500 mt-1">Add a host to start running agents</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"
          >
            Add Your First Host
          </button>
        </div>
      )}

      {/* Host list */}
      {hosts.length > 0 && (
        <div className="space-y-3">
          {hosts.map((host) => (
            <HostItem
              key={host.id}
              host={host}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onValidate={handleValidate}
              isValidating={validatingId === host.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
